import {
  CartesianGrid,
  Line,
  LineChart,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import type { CompositionSeries } from '../../lib/dashboard/composition';
import { CoachCard } from '../concept/coach-card';

/**
 * Two diverging lines in kilograms, not three percentages — percentages move
 * even when nothing happened to the tissue.
 *
 * One y-axis on purpose: fat and lean share a unit, so they share a scale.
 */
export const FatLeanChart: React.FC<{
  series: CompositionSeries;
  goalFatKg: number | null;
}> = ({ series, goalFatKg }) => {
  const data = series.points.map((p) => ({
    date: p.date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' }),
    lean: Number(p.leanKg.toFixed(1)),
    fat: Number(p.fatKg.toFixed(1)),
  }));

  return (
    <CoachCard title={`Fat vs lean · ${data.length} scans with composition`}>
      <div className="h-[190px] w-full">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={data} margin={{ top: 8, right: 8, bottom: 0, left: -18 }}>
            <CartesianGrid stroke="hsl(var(--border))" strokeDasharray="3 3" vertical={false} />
            <XAxis
              dataKey="date"
              tick={{ fontSize: 9, fill: 'hsl(var(--muted-foreground))' }}
              tickLine={false}
              axisLine={{ stroke: 'hsl(var(--border))' }}
            />
            <YAxis
              // Framed to the data, not anchored at zero: the point of this
              // chart is the gap between the lines changing, which a 0-80kg
              // axis flattens into two near-flat rules.
              domain={[
                (min: number) => Math.floor((min - 4) / 5) * 5,
                (max: number) => Math.ceil((max + 4) / 5) * 5,
              ]}
              unit=" kg"
              width={56}
              tick={{ fontSize: 9, fill: 'hsl(var(--muted-foreground))' }}
              tickLine={false}
              axisLine={false}
            />
            <Tooltip
              contentStyle={{
                borderRadius: 8,
                border: '1px solid hsl(var(--border))',
                fontSize: 12,
              }}
              formatter={(v: number, n: string) => [`${v} kg`, n === 'lean' ? 'Lean' : 'Fat']}
            />
            {goalFatKg !== null && (
              <ReferenceLine
                y={goalFatKg}
                stroke="hsl(var(--projected-line))"
                strokeDasharray="5 5"
                label={{
                  value: `Goal ${goalFatKg.toFixed(1)}`,
                  position: 'right',
                  fontSize: 9,
                  fill: 'hsl(var(--projected))',
                }}
              />
            )}
            {/* No entry animation: a coach reading a dashboard wants the
                data, not a reveal — and it makes the e2e capture deterministic
                rather than a race against a 1.5s draw. */}
            <Line
              type="monotone"
              dataKey="lean"
              name="Lean"
              stroke="hsl(var(--primary))"
              strokeWidth={2}
              dot={false}
              isAnimationActive={false}
            />
            <Line
              type="monotone"
              dataKey="fat"
              name="Fat"
              stroke="hsl(var(--foreground))"
              strokeWidth={2}
              dot={false}
              isAnimationActive={false}
            />
          </LineChart>
        </ResponsiveContainer>
      </div>
      <div className="flex gap-4 text-[11px] text-muted-foreground">
        <span className="flex items-center gap-1.5">
          <span className="h-0.5 w-3 bg-primary" /> Lean
        </span>
        <span className="flex items-center gap-1.5">
          <span className="h-0.5 w-3 bg-foreground" /> Fat
        </span>
        {goalFatKg !== null && (
          <span className="flex items-center gap-1.5 text-[hsl(var(--projected))]">
            <span className="h-0.5 w-3 border-t border-dashed border-[hsl(var(--projected-line))]" /> Goal
          </span>
        )}
      </div>
    </CoachCard>
  );
};
